import React, { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { api, formatErr } from "../../../lib/api";
import Field from "../components/Field";
import { formatLocalDate } from "../utils/formatLocalDate";

/**
 * Hours overrides section — date-range schedule overrides on specific weekdays.
 *
 * Use case: "Orario estivo Lun-Ven 09:00–16:00" between 1 Jul and 31 Aug.
 * Distinct from Holidays (full-day closures) and Blocks (datetime ranges).
 *
 * Extracted from BusinessDashboardShell.jsx (iter28 refactor step 2.7).
 * No logic, UX or API change — only the file location.
 *
 * Capability: read gated by `appointments:view` (managers/staff can see).
 * Mutations (POST/DELETE) stay owner-only — see backend
 * `routes/hours_overrides.py`. UI gating is at the shell level via
 * `hours:manage`.
 *
 * Rendered as a sibling section inside `WorkingHours.jsx`.
 */

// Local-only helpers — `DOW_LABELS_SHORT`, `minToHHMM`, `HHMMtoMin` are
// private to this tab.
const DOW_LABELS_SHORT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

function minToHHMM(m) {
  if (m == null) return "—";
  const h = Math.floor(m / 60), mm = m % 60;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

function HHMMtoMin(s) {
  if (!s) return null;
  const [h, m] = s.split(":").map((x) => parseInt(x, 10));
  if (Number.isNaN(h)) return null;
  return h * 60 + (m || 0);
}

export default function HoursOverridesSection({ businessId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  async function load() {
    setLoading(true);
    const { data } = await api.get(`/businesses/${businessId}/hours-overrides`);
    setItems(data || []);
    setLoading(false);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [businessId]);

  async function del(id) {
    if (!window.confirm("Eliminare questo orario speciale?")) return;
    await api.delete(`/businesses/${businessId}/hours-overrides/${id}`);
    load();
  }

  return (
    <div className="mt-10 pt-8 border-t border-zinc-200 dark:border-zinc-700" data-testid="overrides-section">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <h4 className="font-display text-xl font-medium tracking-tight">Orari speciali</h4>
          <p className="text-sm text-zinc-500 mt-1 max-w-md dark:text-zinc-400">
            Modifiche temporanee dell'orario di apertura su un periodo specifico. Esempio: <em>"Orario estivo Lun-Ven 09:00-16:00"</em> dal 1 luglio al 31 agosto. Sostituisce gli orari standard nei giorni indicati.
          </p>
        </div>
        <button onClick={() => setAdding(true)} className="btn-accent h-10 px-4 text-sm" data-testid="overrides-add-btn">+ Aggiungi orario speciale</button>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500 dark:text-zinc-400">Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="card-soft p-6 text-center text-sm text-zinc-500 dark:text-zinc-400" data-testid="overrides-empty">
          Nessun orario speciale. Aggiungine uno per gestire ferie estive, orari ridotti o aperture straordinarie.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((o) => (
            <div key={o.id} className="card-soft p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`override-row-${o.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold">{o.label}</div>
                <div className="text-xs text-zinc-600 mt-0.5 dark:text-zinc-400">
                  {formatLocalDate(o.date_from)} → {formatLocalDate(o.date_to)}
                  {" · "}
                  <strong>{minToHHMM(o.open_min)}–{minToHHMM(o.close_min)}</strong>
                  {" · "}
                  Giorni: {(o.active_dows || []).map((d) => DOW_LABELS_SHORT[d]).join(", ")}
                </div>
              </div>
              <button onClick={() => del(o.id)} className="h-8 w-8 rounded-full bg-rose-50 text-rose-600 hover:bg-rose-100 flex items-center justify-center dark:bg-rose-950/40" data-testid={`override-del-${o.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {adding && (
        <OverrideAddModal businessId={businessId} onClose={() => setAdding(false)} onCreated={() => { setAdding(false); load(); }} />
      )}
    </div>
  );
}

function OverrideAddModal({ businessId, onClose, onCreated }) {
  const [label, setLabel] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [open, setOpen] = useState("09:00");
  const [close, setClose] = useState("13:00");
  const [dows, setDows] = useState([0, 1, 2, 3, 4]); // Lun-Ven default
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  function toggleDow(d) {
    setDows((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort());
  }

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (dows.length === 0) return setErr("Seleziona almeno un giorno della settimana.");
    if (!from || !to) return setErr("Inserisci data inizio e fine.");
    setBusy(true);
    try {
      await api.post(`/businesses/${businessId}/hours-overrides`, {
        label: label.trim(),
        date_from: from,
        date_to: to,
        active_dows: dows,
        open_min: HHMMtoMin(open),
        close_min: HHMMtoMin(close),
      });
      onCreated();
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || "Errore");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="override-add-modal">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6 dark:bg-zinc-900" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-600">Orari speciali</div>
            <h3 className="font-display text-2xl font-medium mt-1">Nuovo periodo</h3>
          </div>
          <Field label="Etichetta">
            <input required maxLength={120} value={label} onChange={(e) => setLabel(e.target.value)} className="input-soft" placeholder="Es. Orario estivo, Apertura straordinaria" data-testid="override-label" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Dal">
              <input type="date" required value={from} onChange={(e) => setFrom(e.target.value)} className="input-soft" data-testid="override-from" />
            </Field>
            <Field label="Al">
              <input type="date" required value={to} onChange={(e) => setTo(e.target.value)} className="input-soft" data-testid="override-to" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Apertura">
              <input type="time" required value={open} onChange={(e) => setOpen(e.target.value)} className="input-soft" data-testid="override-open" />
            </Field>
            <Field label="Chiusura">
              <input type="time" required value={close} onChange={(e) => setClose(e.target.value)} className="input-soft" data-testid="override-close" />
            </Field>
          </div>
          <Field label="Giorni della settimana applicabili">
            <div className="flex flex-wrap gap-1.5 mt-1">
              {DOW_LABELS_SHORT.map((d, i) => (
                <button type="button" key={i} onClick={() => toggleDow(i)} className={`h-9 px-3 rounded-full text-xs font-medium border transition ${dows.includes(i) ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"}`} data-testid={`override-dow-${i}`}>
                  {d}
                </button>
              ))}
            </div>
          </Field>
          {err && <div className="text-xs text-rose-700 dark:text-rose-300">{err}</div>}
          <button type="submit" disabled={busy} className="btn-accent w-full" data-testid="override-submit">
            {busy ? "Salvo…" : "Aggiungi orario speciale"}
          </button>
        </form>
      </div>
    </div>
  );
}
